import React from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { Anchor, isExternalLink } from '../utils/StringUtils';
import styles from './Markdown.scss';

// We use this custom link renderer to allow for smoohter
// navigation within the app. Also for better handle external links
const MarkdownLink = props => {
  const { href, children } = props;
  if (!href) {
    return <span className={styles.invalidHrefMapping}>{children}</span>;
  }
  // Automatically open external links in a new window
  // with the appropriate html-attributes set.
  if (isExternalLink(href)) {
    return <Anchor href={href}>{children}</Anchor>;
  }
  if (typeof href === 'function') {
    return (
      <button className={styles.linkButton} onClick={href}>
        {children}
      </button>
    );
  }
  return <Link to={href}>{children}</Link>;
};

MarkdownLink.propTypes = {
  href: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  children: PropTypes.node,
};

const propTypes = {
  source: PropTypes.string,
  links: PropTypes.array,
  className: PropTypes.string,
  // Make use of Accedo One styling guidelines
  styled: PropTypes.bool,
  // Set more airy line-height (1.6 usually the browser default)
  bodyStyle: PropTypes.bool,
  themeName: PropTypes.oneOf(['default', 'large']),
  useCustomRenderers: PropTypes.bool,
};

/**
 * Render Markdown with string replacements
 * Use `<br>` in the translations file to make soft breaks.
 * Use `\n\n` in the translations file to create a new paragraph.
 * @param {Object} props
 */
const Markdown = props => {
  const {
    source,
    styled = false,
    bodyStyle = false,
    themeName = 'default',
    links,
    useCustomRenderers = true,
    className,
  } = props;
  const inputSource = source;

  const markdownProps = {
    source: inputSource,
    className: classNames(styles.root, className, {
      [styles.styled]: styled && themeName === 'default',
      [styles.styledBig]: styled && themeName === 'large',
      [styles.bodyStyle]: bodyStyle,
    }),
  };

  const renderers = {
    /**
     * To allow markdown links to trigger a function or action we use the special
     * syntax `%n%` (where n equals to a number 0 - 9).
     * The number should match a function with the index of n in the `links` array.
     */
    // eslint-disable-next-line
    link: ({ href, children }) => {
      const FN_REPLACEMENT_REGEX = /(%(\d)%)/g;
      const matchResult = FN_REPLACEMENT_REGEX.exec(href);
      let link = href;
      if (matchResult && links) {
        const linkIndex = matchResult[2]; // 0 - full match, 1 - first capture group, 2 - Second capture group
        link = links[linkIndex];
      }
      return <MarkdownLink href={link}>{children}</MarkdownLink>;
    },
  };
  if (useCustomRenderers) {
    markdownProps.renderers = renderers;
  }

  return <ReactMarkdown {...markdownProps} />;
};

Markdown.propTypes = propTypes;

export default Markdown;



// WEBPACK FOOTER //
// ./src/client/src/components/Markdown.js